import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from "@angular/core";
import { MatTooltipModule } from "@angular/material/tooltip";

import {
  AvatarColorSlot,
  avatarColorSlotForEmail,
  avatarInitialsForParticipant,
} from "./participant-avatar-color";

/**
 * One row in the avatar stack. Shape matches PRD 001 §7.6 participant
 * but is structurally typed so feature folders can reuse without
 * importing each other's domain types.
 */
export interface ParticipantStackEntry {
  readonly email: string;
  readonly displayName?: string | null;
  /**
   * PRD 017 §6.11 — when true, the avatar renders a red bounce badge
   * with a tooltip explaining the address is paused/suppressed.
   */
  readonly isBounced?: boolean;
}

const DEFAULT_VISIBLE_COUNT = 3;

interface AvatarSlot {
  readonly key: string;
  readonly displayName: string;
  readonly email: string;
  readonly initials: string;
  readonly colorClass: string;
  readonly tooltip: string;
  readonly isBounced: boolean;
  readonly bounceTooltip: string;
}

const BOUNCE_TOOLTIP =
  "Address is paused/suppressed — messages will not be delivered.";

/**
 * PRD 001 §8 — Participant avatar stack. Renders up to N avatars
 * side-by-side with deterministic per-email colors (PRD 001 §7.3),
 * plus a `+N` overflow chip when the participant count exceeds the
 * visible slot count. The overflow chip's tooltip enumerates the
 * hidden participants for assistive tech.
 */
@Component({
  selector: "shared-participant-stack",
  standalone: true,
  imports: [CommonModule, MatTooltipModule],
  templateUrl: "./participant-stack.component.html",
  styleUrls: ["./participant-stack.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParticipantStackComponent {
  readonly participants = input.required<readonly ParticipantStackEntry[]>();
  readonly visibleCount = input<number>(DEFAULT_VISIBLE_COUNT);

  readonly visibleSlots = computed<readonly AvatarSlot[]>(() =>
    this.participants()
      .slice(0, this.visibleCount())
      .map((p) => this.toSlot(p)),
  );

  readonly overflowCount = computed<number>(() =>
    Math.max(0, this.participants().length - this.visibleCount()),
  );

  readonly overflowTooltip = computed<string>(() =>
    this.participants()
      .slice(this.visibleCount())
      .map((p) => this.tooltipText(p))
      .join(", "),
  );

  private toSlot(p: ParticipantStackEntry): AvatarSlot {
    const display = (p.displayName ?? "").trim() || p.email;
    return {
      key: p.email.toLowerCase(),
      displayName: display,
      email: p.email,
      initials: avatarInitialsForParticipant(p.displayName, p.email),
      colorClass: this.toColorClass(avatarColorSlotForEmail(p.email)),
      tooltip: this.tooltipText(p),
      isBounced: p.isBounced === true,
      bounceTooltip: BOUNCE_TOOLTIP,
    };
  }

  private toColorClass(slot: AvatarColorSlot): string {
    return `shared-participant-stack__avatar--${slot}`;
  }

  private tooltipText(p: ParticipantStackEntry): string {
    const display = (p.displayName ?? "").trim();
    return display ? `${display} <${p.email}>` : p.email;
  }
}
